import { BrainTreeScripts} from "~/types/braintree.type";


export class PaypalStrategy  {
  private paypal_instance;
  private data_collector_instance;

  async  loadBraintreeScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
      document.head.appendChild(script);
    });
  }


  async initializePayment(
      client,
      data_collector
  ): Promise<void> {
    try {


      const braintree = (window as any).braintree;

      if(!(window as any).braintree || !(window as any).braintree.paypalCheckout){
        await this.loadBraintreeScript(BrainTreeScripts.paypal);
        await this.loadBraintreeScript(BrainTreeScripts.client);
      }

      this.paypal_instance = await braintree.paypalCheckout.create({
        client: client,
      });

      this.data_collector_instance = data_collector;

      await this.setupPaypalButton();
      const paypalButtonContainer = document.getElementById('paypal-buttons-container');
      if(!paypalButtonContainer) return;
      const iframe = paypalButtonContainer.querySelector('iframe');
      if(!iframe) return;
      // automation testing
      iframe.setAttribute('data-testid', 'checkout-paypal-pay-button');
    } catch (e) {
      const { $customClientErrorHandler } = useNuxtApp();
      const errorMeta = {client, data_collector, paypalServiceError: true}
      
      $customClientErrorHandler(
        e,
        'high',
        'Paypal Service Error - trying to initialize payment using initializePayment()',
        errorMeta
      );
    }
  }

  private async setupPaypalButton(): Promise<void> {
    if (!this.paypal_instance) {
      return
    }

    try {
      const paypal_sdk = await this.paypal_instance.loadPayPalSDK({
        currency: "EUR",
        components: "buttons,messages",
        "enable-funding": "paylater",
      });

      const fundingSources = [
        String(window.paypal.FUNDING.PAYPAL),
        String(window.paypal.FUNDING.CREDIT),
        "paylater",
      ];

      const authStore = useAuthStore()
      let user = authStore.user ?? authStore.guest;
      const checkoutStore = useCheckoutStore()
      const trolleyStore = useTrolleyStore()

      fundingSources.forEach((fundingSource) => {
        const settings = {
          createOrder: async (): Promise<string> => {
            try {
              checkoutStore.is_transaction_loading = true
              checkoutStore.resetError()
              return await paypal_sdk.createPayment({
                locale: "en-GB",
                flow: "checkout",
                amount: trolleyStore.trolley.order_response?.totals?.inc_vat.raw.toString(), // Adjust as needed
                currency: "EUR",
                enableShippingAddress: true,
                shippingAddressEditable: false,
                shippingAddressOverride: {
                  recipientName: user.first_name,
                  line1: checkoutStore.billing_address.line_1,
                  line2: checkoutStore.billing_address.line_2,
                  city: checkoutStore.billing_address.town,
                  state: checkoutStore.billing_address.country,
                  postalCode: checkoutStore.billing_address.postcode,
                  countryCode: 'IN',
                },
              });
            } catch (err) {

              throw err; // Ensure the promise is rejected
              useCheckoutStore().is_transaction_loading = false
            }
          },

          onApprove: async (data: any, actions: any) => {
            try {
              const payload = await this.paypal_instance!.tokenizePayment(data);
              useCheckoutStore().is_transaction_loading = true
              useCheckoutStore().payment_success = 'Payment successful redirecting,,'
              useCheckoutStore().createTransaction(payload.nonce, "braintree-paypal-normal")
            } catch (err) {
              checkoutStore.payment_error.paypal_error = useTranslation('paypalPaymentFailed' , 'Paypal-betaling mislukt')
              useCheckoutStore().is_transaction_loading = false
            }
          },

          onCancel: () => {

            useCheckoutStore().payment_error.paypal_error = useTranslation('paypalPaymentCancelled' , 'PayPal-betaling geannuleerd')
            useCheckoutStore().is_transaction_loading = false
          },

          onError: (err: string) => {
            useCheckoutStore().payment_error.paypal_error = useTranslation('paypalPaymentFailed' , 'Paypal-betaling mislukt')
            useCheckoutStore().is_transaction_loading = false
          },

          style: {
            height: 40,
            "z-index": 0,
            ...(fundingSource === "paylater" ? { color: "gold" } : {}),
          },
        };

        const button = window.paypal.Buttons({
          fundingSource,
          ...settings,
        });

        if (!button.isEligible()) {
          console.warn(`Funding source ${fundingSource} is not eligible.`);
          return;
        }

        button.render("#paypal-buttons-container");
      });
    } catch (err) {
      useCheckoutStore().payment_error.paypal_error = useTranslation('paypalPaymentFailed' , 'Paypal-betaling mislukt')
      useCheckoutStore().is_transaction_loading = false
    }
  }



}
